import type { ComponentDefinition, FormComponent, FormDocument, FormParameters, FormTemplate } from '@formbird/types';
import { createSignal, createEffect, onCleanup } from 'solid-js';
import { Subscription } from 'rxjs';

interface CheckBoxProps {
  document: FormDocument;
  template: FormTemplate;
  componentDefinition: ComponentDefinition;
  fieldValue: any;
  fieldName: string;
  previousFieldValue: any;
  key: number;
  formParameters: FormParameters;
}

export function CheckBox(props: CheckBoxProps) {
  const subs = new Subscription();

  const [checked, setChecked] = createSignal(false);
  const [componentDef, setComponentDef] = createSignal(props.componentDefinition);

  const changedDocumentService = (globalThis as any).FormbirdServiceInjector.get('ChangedDocumentService');

  // Helper to check boolean value logic from Angular component
  const checkBool = (val: any): boolean => {
    if (typeof val === 'boolean') return val;
    if (typeof val === 'object' && val !== null && typeof val.valueOf() === 'boolean') return val.valueOf();
    return (/true/).test(String(val));
  };

  // Initialize value
  createEffect(() => {
    const val = props.fieldValue !== undefined ? props.fieldValue : props.document[props.fieldName];
    setChecked(checkBool(val));
  });

  const docSub = changedDocumentService
    .watch(props.fieldName, props.document.documentId, null)
    .subscribe((newValue: any) => {
      setChecked(checkBool(newValue));
    });
  subs.add(docSub);

  const compSub = changedDocumentService
    .watchComponentDef(props.fieldName, props.template.documentId)
    .subscribe((compDef: any) => {
      setComponentDef({ ...componentDef(), ...compDef });
    });
  subs.add(compSub);

  onCleanup(() => {
    subs.unsubscribe();
  });

  const isDisabled = () => componentDef()?.enabled === false;

  const handleChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const newValue = target.checked;
    setChecked(newValue);

    const changeInfo: FormComponent = {
      document: props.document,
      template: props.template,
      componentDefinition: componentDef(),
      fieldValue: newValue,
      fieldName: props.fieldName,
      formParameters: props.formParameters,
      key: props.key,
    };

    changedDocumentService.valueChanged(changeInfo, newValue);
  };

  return (
    <div class="check-box-class component-wrap">
      <div class="flex flex-col gap-1">
        <div style="display: flex; align-items: center; overflow: hidden; min-width: 0;">
          <span
            class={`sb-check-box-label${componentDef()?.mandatory ? ' ft-mandatory-label' : ''}`}
            title={componentDef()?.label}
          >
            {componentDef()?.label}
            {componentDef()?.mandatory && ' *'}
          </span>
        </div>

        <label class="relative flex items-center cursor-pointer">
          <input
            type="checkbox"
            class="custom-checkbox"
            id={`${componentDef()?.name}${props.document.documentId}`}
            name={componentDef()?.name}
            checked={checked()}
            onChange={handleChange}
            disabled={isDisabled()}
          />
        </label>
      </div>
    </div>
  );
}
